/**
 * French commune autocomplete — search + validation logic, backed by the public
 * `geo.api.gouv.fr/communes` endpoint (no key, JSON).
 *
 * One input field accepts either a partial postal code or a city name:
 *  - input starting with a digit  → postal-code search (by département, then a
 *    client-side prefix filter on `codesPostaux`);
 *  - otherwise                    → city search (by name, boosted by population).
 *
 * Everything here is framework-agnostic and side-effect free except the small
 * network layer at the bottom (which is injectable for testing). The reactive
 * wiring (debounce, abort, blur) lives in `useCommuneAutocomplete`.
 */

export const GEO_API_BASE = 'https://geo.api.gouv.fr/communes'
const COMMUNE_FIELDS = 'nom,codesPostaux,population'

/** Minimum input length before hitting the API (avoids huge, useless queries). */
export const MIN_POSTAL_DIGITS = 3
export const MIN_CITY_CHARS = 3

/** A postal code is at most 5 digits. */
const POSTAL_CODE_LENGTH = 5

/** Hard cap on suggestions returned to the UI. */
export const MAX_SUGGESTIONS = 50

/** Raw commune as returned by geo.api.gouv.fr (with the fields we request). */
export interface GeoCommune {
  nom: string
  /** INSEE commune code. */
  code: string
  codesPostaux?: string[]
  population?: number
}

/** One (commune × postal code) suggestion shown in the list. */
export interface CommuneSuggestion {
  /** Stable de-duplication key: `<communeCode>-<postalCode>`. */
  key: string
  city: string
  postalCode: string
  /** Display + stored label: `<postalCode> <city>`. */
  label: string
  communeCode: string
  population: number
}

/** Minimal payload stored once a commune is chosen / validated. */
export interface CommuneSelection {
  postalCode: string
  city: string
  communeCode: string
  label: string
}

export type CommuneValidationError =
  | 'commune-not-found'
  | 'postal-not-found'
  | 'city-postal-mismatch'

export type CommuneValidationResult =
  | { ok: true; selection: CommuneSelection }
  | { ok: false; error: CommuneValidationError }

/** Search descriptor: which mode and which API URLs to call. `null` means the
 *  input is below the minimum threshold and must not trigger any request. */
export interface CommuneSearchRequest {
  mode: 'postal' | 'city'
  /** Numeric prefix to filter `codesPostaux` against (postal mode only). */
  postalPrefix?: string
  urls: string[]
}

/* ----------------------------------------------------------------
 * Input classification & normalization
 * ----------------------------------------------------------------*/

/** A postal-code search is triggered when the (trimmed) input starts with a digit. */
export function isPostalCodeSearch(input: string): boolean {
  return /^\s*\d/.test(input)
}

/** Keep digits only, capped at 5 characters. */
export function normalizePostalCode(input: string): string {
  return input.replace(/\D/g, '').slice(0, POSTAL_CODE_LENGTH)
}

/** Lower-case, strip accents and collapse whitespace — for case/accent/space
 *  insensitive city comparison. */
export function normalizeCityName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Départements to query for a given numeric postal prefix.
 *  - `20…`  → Corsica is split into `2A` / `2B` (no numeric département code),
 *             so we always query both and let the prefix filter sort it out.
 *  - `97…`  → overseas: query the known DOM codes until the 3rd digit pins one.
 *  - `98…`  → overseas collectivities: same idea with the 98x codes.
 *  - else   → the first two digits are the département code.
 */
export function departmentCodesForPrefix(digits: string): string[] {
  if (digits.startsWith('20')) return ['2A', '2B']
  if (digits.startsWith('97')) {
    return digits.length >= 3 ? [digits.slice(0, 3)] : ['971', '972', '973', '974', '976']
  }
  if (digits.startsWith('98')) {
    return digits.length >= 3 ? [digits.slice(0, 3)] : ['984', '986', '987', '988']
  }
  return [digits.slice(0, 2)]
}

function postalUrl(codeDepartement: string): string {
  const params = new URLSearchParams({
    codeDepartement,
    fields: COMMUNE_FIELDS,
    format: 'json',
  })
  return `${GEO_API_BASE}?${params}`
}

function cityUrl(nom: string): string {
  const params = new URLSearchParams({
    nom,
    fields: COMMUNE_FIELDS,
    boost: 'population',
    format: 'json',
  })
  return `${GEO_API_BASE}?${params}`
}

/**
 * Build the search request for an input, or `null` when below the minimum
 * threshold (no request should be fired). Postal searches may span several
 * URLs (Corsica, overseas) that are merged client-side.
 */
export function buildSearchRequest(rawInput: string): CommuneSearchRequest | null {
  const trimmed = rawInput.trim()
  if (!trimmed) return null

  if (isPostalCodeSearch(trimmed)) {
    const digits = normalizePostalCode(trimmed)
    if (digits.length < MIN_POSTAL_DIGITS) return null
    const deps = departmentCodesForPrefix(digits)
    return { mode: 'postal', postalPrefix: digits, urls: deps.map(postalUrl) }
  }

  if (trimmed.length < MIN_CITY_CHARS) return null
  return { mode: 'city', urls: [cityUrl(trimmed)] }
}

/* ----------------------------------------------------------------
 * Suggestion building
 * ----------------------------------------------------------------*/

/**
 * Flatten communes into (commune × postal code) suggestions:
 *  - in postal mode, keep only postal codes matching the typed prefix;
 *  - de-duplicate by key, sort by population desc → city → postal code;
 *  - cap at MAX_SUGGESTIONS.
 */
export function buildSuggestions(
  communes: readonly GeoCommune[],
  options: { postalPrefix?: string } = {},
): CommuneSuggestion[] {
  const { postalPrefix } = options
  const seen = new Set<string>()
  const out: CommuneSuggestion[] = []

  for (const commune of communes) {
    for (const postalCode of commune.codesPostaux ?? []) {
      if (postalPrefix && !postalCode.startsWith(postalPrefix)) continue
      const key = `${commune.code}-${postalCode}`
      if (seen.has(key)) continue
      seen.add(key)
      out.push({
        key,
        city: commune.nom,
        postalCode,
        label: `${postalCode} ${commune.nom}`,
        communeCode: commune.code,
        population: commune.population ?? 0,
      })
    }
  }

  out.sort(
    (a, b) =>
      b.population - a.population ||
      a.city.localeCompare(b.city, 'fr') ||
      a.postalCode.localeCompare(b.postalCode),
  )

  return out.slice(0, MAX_SUGGESTIONS)
}

/** Suggestion → minimal stored selection. */
export function suggestionToSelection(s: CommuneSuggestion): CommuneSelection {
  return { postalCode: s.postalCode, city: s.city, communeCode: s.communeCode, label: s.label }
}

function communeToSelection(commune: GeoCommune, postalCode: string): CommuneSelection {
  return {
    postalCode,
    city: commune.nom,
    communeCode: commune.code,
    label: `${postalCode} ${commune.nom}`,
  }
}

/* ----------------------------------------------------------------
 * Final validation (FR only) against the list of real communes
 * ----------------------------------------------------------------*/

/**
 * Validate a free-typed value against the communes fetched for it. Accepts
 * three shapes:
 *  - `75001 Paris` → the postal code must exist AND the city must match it;
 *  - `Paris`       → a commune must match the name (case/accent/space insensitive);
 *  - `75001`       → the postal code must exist.
 */
export function validateCommuneValue(
  rawValue: string,
  communes: readonly GeoCommune[],
): CommuneValidationResult {
  const value = rawValue.trim()

  // "75001 Paris" — postal code followed by a city name.
  const postalCity = value.match(/^(\d{5})\s+(.+)$/)
  if (postalCity) {
    const [, postalCode, cityPart] = postalCity
    const matchingPostal = communes.filter((c) => (c.codesPostaux ?? []).includes(postalCode))
    if (matchingPostal.length === 0) return { ok: false, error: 'postal-not-found' }
    const wanted = normalizeCityName(cityPart)
    const hit = matchingPostal.find((c) => normalizeCityName(c.nom) === wanted)
    if (!hit) return { ok: false, error: 'city-postal-mismatch' }
    return { ok: true, selection: communeToSelection(hit, postalCode) }
  }

  // "75001" — postal code only.
  if (/^\d+$/.test(value)) {
    const hit = communes.find((c) => (c.codesPostaux ?? []).includes(value))
    if (!hit) return { ok: false, error: 'postal-not-found' }
    return { ok: true, selection: communeToSelection(hit, value) }
  }

  // "Paris" — city name only.
  const wanted = normalizeCityName(value)
  const hit = communes.find((c) => normalizeCityName(c.nom) === wanted)
  if (!hit) return { ok: false, error: 'commune-not-found' }
  const postalCode = (hit.codesPostaux ?? [])[0] ?? ''
  return { ok: true, selection: communeToSelection(hit, postalCode) }
}

/* ----------------------------------------------------------------
 * Network layer (injectable `fetchFn` for tests)
 * ----------------------------------------------------------------*/

type FetchLike = typeof fetch

interface FetchOptions {
  signal?: AbortSignal
  /** Network timeout in ms. */
  timeoutMs?: number
  fetchFn?: FetchLike
}

const DEFAULT_TIMEOUT_MS = 5000

/** Combine the caller's abort signal with a per-request timeout signal. */
function withTimeout(signal: AbortSignal | undefined, timeoutMs: number) {
  const timeoutController = new AbortController()
  const timer = setTimeout(() => timeoutController.abort(), timeoutMs)
  const signals = [timeoutController.signal]
  if (signal) signals.push(signal)
  // AbortSignal.any is available in modern browsers and Node ≥ 20.
  const combined = AbortSignal.any(signals)
  return { signal: combined, clear: () => clearTimeout(timer) }
}

async function fetchCommunesFromUrl(url: string, options: FetchOptions): Promise<GeoCommune[]> {
  const { signal, timeoutMs = DEFAULT_TIMEOUT_MS, fetchFn = fetch } = options
  const { signal: combined, clear } = withTimeout(signal, timeoutMs)
  try {
    const res = await fetchFn(url, { signal: combined })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = (await res.json()) as GeoCommune[]
    return Array.isArray(data) ? data : []
  } finally {
    clear()
  }
}

/** Fetch + merge communes for a search request (parallel for multi-département). */
export async function fetchCommunes(
  request: CommuneSearchRequest,
  options: FetchOptions = {},
): Promise<GeoCommune[]> {
  const lists = await Promise.all(request.urls.map((url) => fetchCommunesFromUrl(url, options)))
  return lists.flat()
}

/** End-to-end search: classify input → fetch → build suggestions. Returns an
 *  empty list when the input is below threshold. */
export async function searchCommunes(
  rawInput: string,
  options: FetchOptions = {},
): Promise<CommuneSuggestion[]> {
  const request = buildSearchRequest(rawInput)
  if (!request) return []
  const communes = await fetchCommunes(request, options)
  return buildSuggestions(communes, { postalPrefix: request.postalPrefix })
}

/** End-to-end validation: classify value → fetch → validate. */
export async function validateCommune(
  rawValue: string,
  options: FetchOptions = {},
): Promise<CommuneValidationResult> {
  const request = buildSearchRequest(rawValue)
  if (!request) return { ok: false, error: 'commune-not-found' }
  const communes = await fetchCommunes(request, options)
  return validateCommuneValue(rawValue, communes)
}
